import noop from "lodash-es/noop";
import React from "react";
import styled from "@emotion/styled";
import { SortIndicator } from "@atlas-int/ui";
import { surface, fontSize, lineHeight, spacing, dataViz } from "@atlas-int/tokens";

const TitleCellContainer = styled.div`
  border-bottom: 2px solid ${dataViz.neutral};
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${spacing[4]};
  text-transform: uppercase;
  position: sticky;
  top: 0;
  background-color: ${surface.white};
  font-size: ${fontSize.base};
  line-height: ${lineHeight.normal};

  &:hover .sort-indicator {
    opacity: 1;
  }

  @media (max-width: 1050px) {
    font-size: ${fontSize.sm};
  }
`;

export enum SortCriterion {
  Complexity,
  OpportunityGain,
  Feasibility,
  ExportValue,
  ExportValueChange,
  RCA,
}

export enum SortDirection {
  Ascending,
  Descending,
}

type TitleCellProps = {
  children?: React.ReactNode;
  stickColumnToLeft?: boolean;
} & (
  | {
      hasAssignedSortCriterion: true;
      assignedSortCriterion: SortCriterion;
      onClick: (criterion: SortCriterion) => void;
      sortCriterion: SortCriterion;
      sortDirection: SortDirection;
      zIndex: number;
      promoteZIndex: () => void;
      restoreZIndex: () => void;
    }
  | {
      hasAssignedSortCriterion: false;
      zIndex: number;
    }
);

const TitleCell: React.FunctionComponent<TitleCellProps> = (props) => {
  let onClick: React.MouseEventHandler;
  let onMouseEnter: React.MouseEventHandler;
  let onMouseLeave: React.MouseEventHandler;
  let style: React.CSSProperties;
  let arrows: React.ReactElement | null;
  if (props.hasAssignedSortCriterion === true) {
    const {
      assignedSortCriterion,
      sortCriterion,
      sortDirection,
      promoteZIndex,
      restoreZIndex,
      zIndex,
    } = props;

    const isActive = sortCriterion === assignedSortCriterion;
    let indicatorDirection: "asc" | "desc" | "none" = "none";
    if (isActive) {
      indicatorDirection =
        sortDirection === SortDirection.Ascending ? "asc" : "desc";
    }

    arrows = (
      <SortIndicator
        active={isActive}
        direction={indicatorDirection}
        className="sort-indicator"
      />
    );
    onClick = () => props.onClick(assignedSortCriterion);
    onMouseEnter = promoteZIndex;
    onMouseLeave = restoreZIndex;
    style = {
      zIndex,
      cursor: "pointer",
    };
  } else {
    onClick = noop;
    onMouseEnter = noop;
    onMouseLeave = noop;
    arrows = null;
    style = {
      zIndex: props.zIndex,
      cursor: "auto",
      left: props.stickColumnToLeft === true ? 0 : undefined,
    };
  }
  return (
    <TitleCellContainer
      role="columnheader"
      onClick={onClick}
      style={style}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {props.children}
      {arrows}
    </TitleCellContainer>
  );
};

export default TitleCell;
